import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { Sparkles, CheckCircle2, AlertCircle, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { API } from '../config/api';

export default function PrivilegeAuditPanel() {
  const [data, setData] = useState({ users: [], total_users: 0, auto_fixed: 0 });
  const [loading, setLoading] = useState(true);
  const [granting, setGranting] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/privilege/audit`, { withCredentials: true });
      setData(res.data);
      if (res.data.auto_fixed > 0) {
        toast.success(`${res.data.auto_fixed} compte(s) corrigé(s) automatiquement.`);
      }
    } catch (e) {
      toast.error("Impossible de charger l'audit.");
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const grant = async (email) => {
    setGranting(email);
    try {
      await axios.post(`${API}/privilege/force-discount/${encodeURIComponent(email)}`, {}, { withCredentials: true });
      toast.success(`-20% activé pour ${email}.`);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Erreur.');
    } finally { setGranting(null); }
  };

  if (loading) {
    return (
      <Card className="border-[#E9DDCC] rounded-2xl">
        <CardContent className="p-6 flex items-center justify-center">
          <Loader2 className="w-5 h-5 animate-spin text-[#C99A55]" />
        </CardContent>
      </Card>
    );
  }

  const flagged = data.users.filter((u) => u.discount_applied).length;
  const not_in_list = data.users.filter((u) => !u.in_privilege_list).length;

  return (
    <Card className="border border-[#C99A55]/30 rounded-2xl bg-gradient-to-br from-[#F3EADA] to-[#F5EFE5]" data-testid="privilege-audit-panel">
      <CardContent className="p-6 space-y-5">
        <div className="flex items-start justify-between gap-3">
          <div>
            <div className="flex items-center gap-2 mb-1">
              <Sparkles className="w-4 h-4 text-[#C99A55]" strokeWidth={1.8} />
              <span className="text-[10px] uppercase tracking-[0.2em] text-[#C99A55] font-semibold">Audit -20%</span>
            </div>
            <h3 className="font-heading text-lg text-[#493329]">Statut Privilège de chaque cliente inscrite</h3>
            <p className="text-xs text-[#7A6455] mt-1">Source de vérité : la liste Privilège. Les comptes manquants sont auto-corrigés à chaque ouverture.</p>
          </div>
          <Button onClick={load} variant="outline" size="sm" className="rounded-full text-xs h-8 border-[#E9DDCC] text-[#7A6455]">
            Actualiser
          </Button>
        </div>

        <div className="grid grid-cols-3 gap-3">
          <div className="bg-white rounded-xl p-3 text-center border border-[#E9DDCC]">
            <div className="text-[10px] uppercase text-[#AE9885] tracking-wider mb-1">Inscrites</div>
            <div className="font-heading text-2xl text-[#493329]">{data.total_users}</div>
          </div>
          <div className="bg-white rounded-xl p-3 text-center border border-[#C99A55]/40">
            <div className="text-[10px] uppercase text-[#C99A55] tracking-wider mb-1">Avec -20%</div>
            <div className="font-heading text-2xl text-[#C99A55]">{flagged}</div>
          </div>
          <div className="bg-white rounded-xl p-3 text-center border border-[#E9DDCC]">
            <div className="text-[10px] uppercase text-[#AE9885] tracking-wider mb-1">Sans privilège</div>
            <div className="font-heading text-2xl text-[#493329]">{not_in_list}</div>
          </div>
        </div>

        {data.auto_fixed > 0 && (
          <div className="flex items-center gap-2 bg-[#FFF8E6] rounded-xl p-3 border border-[#C99A55]/40 text-xs text-[#493329]">
            <CheckCircle2 className="w-4 h-4 text-[#C99A55]" />
            {data.auto_fixed} compte(s) ont été automatiquement corrigés (-20% appliqué).
          </div>
        )}

        <div className="bg-white rounded-xl border border-[#E9DDCC] max-h-[420px] overflow-y-auto">
          <table className="w-full text-sm">
            <thead className="bg-[#F5EFE5] sticky top-0">
              <tr className="text-[10px] uppercase tracking-wider text-[#AE9885]">
                <th className="px-4 py-2 text-left font-semibold">Cliente</th>
                <th className="px-2 py-2 text-left font-semibold">Formule</th>
                <th className="px-2 py-2 text-left font-semibold">Tarif</th>
                <th className="px-2 py-2 text-center font-semibold">-20%</th>
                <th className="px-2 py-2 text-right font-semibold">Action</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[#EFE4D0]">
              {data.users.length === 0 ? (
                <tr><td colSpan={5} className="text-center text-[#AE9885] italic py-6">Aucune cliente inscrite.</td></tr>
              ) : data.users.map((u, i) => (
                <tr key={i}>
                  <td className="px-4 py-2.5">
                    <div className="text-[#493329] font-medium truncate">{u.name || '—'}</div>
                    <div className="text-[10px] text-[#7A6455] truncate">{u.email}</div>
                  </td>
                  <td className="px-2 py-2.5 text-[#7A6455] capitalize">{u.plan || '—'}</td>
                  <td className="px-2 py-2.5">
                    {u.discount_applied ? (
                      <span>
                        <span className="text-[10px] text-[#AE9885] line-through mr-1">{u.std_price}€</span>
                        <span className="text-[#C99A55] font-semibold">{u.final_price}€</span>
                      </span>
                    ) : (
                      <span className="text-[#493329]">{u.final_price ? `${u.final_price}€` : '—'}</span>
                    )}
                  </td>
                  <td className="px-2 py-2.5 text-center">
                    {u.discount_applied
                      ? <CheckCircle2 className="w-4 h-4 text-[#C99A55] inline" />
                      : <AlertCircle className="w-4 h-4 text-[#AE9885] inline" />}
                  </td>
                  <td className="px-2 py-2.5 text-right">
                    {!u.discount_applied && (
                      <Button
                        size="sm"
                        onClick={() => grant(u.email)}
                        disabled={granting === u.email}
                        className="bg-[#C99A55] hover:bg-[#493329] text-white rounded-full text-[10px] h-7 px-3"
                        data-testid={`force-discount-${u.email}`}
                      >
                        {granting === u.email ? <Loader2 className="w-3 h-3 animate-spin" /> : 'Activer -20%'}
                      </Button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <p className="text-[11px] text-[#AE9885] italic">
          L'auto-correction tourne aussi à chaque démarrage du serveur. Aucune cliente Privilège ne peut rester sans -20% durablement.
        </p>
      </CardContent>
    </Card>
  );
}
